<template>
  <vinnuskoli-page>
    <b-row>
      <b-col>
        <h5 class="page-title">Viðvera</h5>
      </b-col>
    </b-row>
    <b-row class="mb-3">
      <b-col cols="auto">
        <b-form-radio-group v-model="mode" buttons button-variant="outline-primary" size="sm">
          <b-form-radio value="day">Dagur</b-form-radio>
          <b-form-radio value="period">Tímabil</b-form-radio>
        </b-form-radio-group>
      </b-col>
    </b-row>

    <!-- Day mode -->
    <template v-if="mode === 'day'">
      <b-row>
        <b-col cols="12" md="auto" class="mb-2 mb-md-0">
          <b-input-group>
            <b-input-group-text slot="prepend" @click.prevent="changeDate(-1)">
              <i class="fa fa-fw fa-angle-left"></i>
            </b-input-group-text>
            <datepicker
              id="date"
              name="date"
              v-model="date"
              :language="lang"
              :monday-first="true"
              format="dd.MM.yyyy"
              :typeable="false"
              ref="datePickr"
              @input="loadGroups"
            ></datepicker>
            <b-input-group-text slot="append" @click.prevent="changeDate(1)">
              <i class="fa fa-fw fa-angle-right"></i>
            </b-input-group-text>
          </b-input-group>
        </b-col>
        <b-col cols="12" md>
          <b-form-select
            id="workplace"
            name="workplace"
            v-model="options.attendance.group"
            v-if="groupList.length > 0"
            @input="validateDate"
          >
            <option :value="null">-- Veldu hóp --</option>
            <option v-for="group in groupList" :key="group.id" :value="group.id">
              {{ group.workplace.name }} - {{ group.name }} {{ group.date_from | moment('DD.MM.YYYY') }}-{{
                group.date_to | moment('DD.MM.YYYY')
              }}
            </option>
          </b-form-select>
          <div v-else>
            <i>Engir hópar eru skráðir á valinn dag, vinsamlegast veljið annan.</i>
          </div>
        </b-col>
      </b-row>
      <b-row class="mt-4">
        <b-col>
          <b-alert :show="studentList.length > 0 && validate && validate.batch">
            <i class="fa fa-info-circle"></i>
            Ath! Ekki er hægt að breyta færslum þar sem búið er að læsa þeim vegna uppgjörs.
          </b-alert>
          <b-card
            no-body
            border-variant="light"
            class="mb-2"
            v-if="options.attendance && options.attendance.group && (loading || studentList.length > 0)"
          >
            <table class="table white-table mb-0">
              <tr>
                <th>Nafn</th>
                <th>Kennitala</th>
                <th>Sérþarfir/Ofnæmi</th>
                <th>
                  Fj. klst.
                  <a
                    v-if="loggedInUserHasAnyPermission('write_attendance') && !(validate && validate.batch) && studentList.length > 0"
                    @click.prevent="openBulkModal"
                    style="cursor: pointer"
                    v-b-tooltip.hover
                    title="Setja fyrir alla"
                  >
                    <i class="fa fa-fw fa-pencil-square-o"></i>
                  </a>
                </th>
                <th>Veikindi (klst.)</th>
                <th v-if="projectList.length > 0">Verkefni</th>
                <th>
                  Heildarfjöldi tíma
                  <i
                    class="fa fa-fw fa-info-circle"
                    v-b-tooltip.hover
                    title="Heildarfjöldi unninna tíma þátttakanda fyrir alla hópa á tímabilinu"
                  ></i>
                </th>
                <th>Fjarvist</th>
                <th>Athugasemd</th>
                <th></th>
              </tr>
              <tr v-if="loading">
                <td colspan="10" class="text-center py-3">
                  <b-spinner small></b-spinner>
                </td>
              </tr>
              <attendance-item
                v-for="student in studentList"
                :key="student.id"
                :item="student"
                :date="date"
                :batch="validate.batch"
                :projectList="projectList"
                :groupId="options.attendance.group"
                @edit="onEdit"
              >
              </attendance-item>
            </table>
          </b-card>
          <div v-else-if="!loading">
            <i v-if="validate && validate.vacation_day">
              Ekki er hægt að skrá viðveru þar sem búið er að skilgreina frídag fyrir valinn dag.
            </i>
            <i v-if="validate && !validate.weekday_valid">
              Ekki er hægt að skrá viðveru þar sem þessi hópur á ekki að vinna á völdum degi.
            </i>
            <i v-if="validate && validate.weekday_valid && !validate.vacation_day && options.attendance.group">
              Engir þátttakendur eru skráðir í valinn hóp, vinsamlegast veljið annan.
            </i>
          </div>
        </b-col>
      </b-row>
    </template>

    <!-- Period mode -->
    <template v-if="mode === 'period'">
      <b-row>
        <b-col cols="12" xl="auto" class="mb-2 mb-xl-0">
          <date-range-filter
            :dateFrom="periodDateFrom"
            :dateTo="periodDateTo"
            idPrefix="period"
            @change="onPeriodDateChange"
          ></date-range-filter>
        </b-col>
        <b-col cols="12" xl>
          <b-form-select v-model="periodGroup" v-if="periodGroupList.length > 0" @input="() => loadPeriodAttendance(1)">
            <option :value="null">-- Veldu hóp --</option>
            <option v-for="group in periodGroupList" :key="group.id" :value="group.id">
              {{ group.workplace.name }} - {{ group.name }} {{ group.date_from | moment('DD.MM.YYYY') }}-{{
                group.date_to | moment('DD.MM.YYYY')
              }}
            </option>
          </b-form-select>
          <div v-else-if="periodDateFrom">
            <i>Engir hópar eru skráðir á valinn dag, vinsamlegast veljið annað tímabil.</i>
          </div>
        </b-col>
      </b-row>
      <b-row class="mt-4">
        <b-col>
          <b-alert :show="mappedPeriodRecords.some(r => r.batch)">
            <i class="fa fa-info-circle"></i>
            Ath! Ekki er hægt að breyta færslum þar sem búið er að læsa þeim vegna uppgjörs.
          </b-alert>
          <div v-if="loadingPeriod" class="text-center my-3">
            <b-spinner></b-spinner>
          </div>
          <template v-else>
            <b-card no-body border-variant="light" class="mb-2" v-if="mappedPeriodRecords.length > 0">
              <table class="table white-table mb-0">
                <tr>
                  <th>Dagsetning</th>
                  <th>Nafn</th>
                  <th>Kennitala</th>
                  <th>Sérþarfir/Ofnæmi</th>
                  <th>Fj. klst.</th>
                  <th>Veikindi (klst.)</th>
                  <th v-if="projectList.length > 0">Verkefni</th>
                  <th>Heildarfjöldi tíma</th>
                  <th>Fjarvist</th>
                  <th>Athugasemd</th>
                  <th></th>
                </tr>
                <attendance-item
                  v-for="record in mappedPeriodRecords"
                  :key="record.attendance_id"
                  :item="record"
                  :date="record.date"
                  :show-date="true"
                  :batch="record.batch"
                  :projectList="projectList"
                  :groupId="periodGroup"
                  @edit="onEdit"
                ></attendance-item>
              </table>
            </b-card>
            <div v-else-if="periodGroup && periodDateFrom && periodDateTo">
              <i>Engar viðverufærslur fundust á völdu tímabili.</i>
            </div>
            <pagination-2
              v-if="periodPagination && periodPagination.totalCount > 0"
              :dataLength="periodRecords.length"
              :totalCount="periodPagination.totalCount"
              :currentPage="periodPagination.currentPage"
              :limit="periodPagination.currentLimit"
              :setCurrentPage="page => loadPeriodAttendance(page, periodLimit)"
              :setLimit="limit => loadPeriodAttendance(1, limit)"
            />
          </template>
        </b-col>
      </b-row>
    </template>
    <attendance-edit-modal ref="editModal" @successful="onEditSuccessful"></attendance-edit-modal>
    <bulk-attendance-modal ref="bulkModal" :projectList="projectList" @save="onBulkSave"></bulk-attendance-modal>
  </vinnuskoli-page>
</template>

<script>
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';
import Datepicker from 'vuejs-datepicker';
import { is } from 'vuejs-datepicker/dist/locale';
import placement from '@/api/placement';
import core from '@/api/core';
import attendance from '@/api/attendance';
import AttendanceItem from '@/components/attendance/AttendanceItem.vue';
import AttendanceEditModal from '@/components/attendance/AttendanceEditModal.vue';
import BulkAttendanceModal from '@/components/attendance/BulkAttendanceModal.vue';
import DateRangeFilter from '@/components/DateRangeFilter.vue';
import { parsePagination, normalizeHoursInput } from '@/utils';
import Pagination2 from '@/components/Pagination2.vue';

export default {
  name: 'Attendance',
  components: {
    Datepicker,
    AttendanceItem,
    AttendanceEditModal,
    BulkAttendanceModal,
    DateRangeFilter,
    Pagination2,
  },
  data() {
    return {
      mode: 'day',
      // Day mode
      date: moment().toDate(),
      lang: is,
      loading: false,
      groupList: [],
      projectList: [],
      studentList: [],
      validate: null,
      options: {},
      // Period mode
      periodDateFrom: moment().startOf('month').toDate(),
      periodDateTo: moment().endOf('month').toDate(),
      periodGroup: null,
      periodGroupList: [],
      periodRecords: [],
      periodDateValidations: {},
      periodPage: 1,
      periodLimit: 25,
      periodPagination: null,
      loadingPeriod: false,
    };
  },
  computed: {
    ...mapGetters(['loggedInUserHasAnyPermission', 'loggedInUserOptions']),
    mappedPeriodRecords() {
      return this.periodRecords.map(record => {
        const v = this.periodDateValidations[record.date] || {};
        return {
          id: record.person.id,
          attendance_id: record.id,
          date: record.date,
          batch: !!(v.batch || record.correction !== null),
          maxHours: v.max_hours ?? null,
          weekOld: v.week_old || false,
          person: record.person.person,
          hours: record.hours,
          sick_hours: record.sick_hours || 0,
          comment: record.comment,
          attendance_type: record.attendance_type,
          attendance_project: record.attendance_project,
          period_hours_total: null,
          period_max_hours: null,
          special_needs: null,
          has_special_needs: false,
        };
      });
    },
  },
  methods: {
    // Day mode methods
    changeDate(change) {
      this.date = moment(this.date).add(change, 'days').toDate();
      this.loadGroups();
    },
    async validateDate() {
      if (this.options.attendance.group) {
        this.loading = true;
        try {
          const response = await placement.placementGroupDateValid(this.options.attendance.group, this.date);
          await this.setLoggedInUserOption({ option: 'attendance', value: this.options.attendance });
          await this.saveLoggedInUserOptions();
          this.validate = response.data;
          this.studentList = [];
          if (this.validate.weekday_valid && !this.validate.vacation_day) {
            await this.loadStudents();
          }
        } catch (e) {
          this.$log.error(e);
          this.displayError(`Óvænt villa (${e.response ? e.response.status : -1}) kom upp. Vinsamlegast reyndu aftur.`);
        } finally {
          this.loading = false;
        }
      } else {
        this.studentList = [];
        await this.setLoggedInUserOption({ option: 'attendance', value: this.options.attendance });
        await this.saveLoggedInUserOptions();
      }
    },
    async loadAttendanceProjects() {
      this.projectList = [];
      try {
        const response = await attendance.attendanceOverviewProjectList();
        this.projectList = response.data;
      } catch (e) {
        this.$log.error(e);
        this.displayError(`Óvænt villa (${e.response ? e.response.status : -1}) kom upp. Vinsamlegast reyndu aftur.`);
      }
    },
    async loadGroups() {
      this.loading = true;
      this.groupList = [];
      try {
        const response = await core.dropdownPlacementgroupList([], this.date);
        this.groupList = response.data;
        if (this.options.attendance.group && !this.groupList.find(g => g.id === this.options.attendance.group)) {
          this.options.attendance.group = null;
          this.studentList = [];
          await this.setLoggedInUserOption({ option: 'attendance', value: this.options.attendance });
          await this.saveLoggedInUserOptions();
        } else if (this.options.attendance.group) {
          await this.validateDate();
        }
      } catch (e) {
        this.$log.error(e);
        this.displayError(`Óvænt villa (${e.response ? e.response.status : -1}) kom upp. Vinsamlegast reyndu aftur.`);
      } finally {
        this.loading = false;
      }
    },
    async loadStudents() {
      this.loading = true;
      this.studentList = [];
      try {
        const response = await attendance.attendanceOverviewList(this.options.attendance.group, this.date);
        this.studentList = response.data.map(x => ({
          id: x.id,
          person: x.person,
          attendance_id: x.attendance ? x.attendance.id : null,
          hours: x.attendance ? x.attendance.hours : '',
          sick_hours: x.attendance ? x.attendance.sick_hours || 0 : 0,
          comment: x.attendance ? x.attendance.comment : '',
          attendance_type: x.attendance && x.attendance.attendance_type ? x.attendance.attendance_type : null,
          attendance_project: x.attendance && x.attendance.attendance_project ? x.attendance.attendance_project : null,
          maxHours: null,
          period_hours_total: x.period_hours_total,
          period_max_hours: x.period_max_hours,
          special_needs: x.special_needs,
          has_special_needs: x.has_special_needs || false,
        }));
      } catch (e) {
        this.$log.error(e);
        this.displayError(`Óvænt villa (${e.response ? e.response.status : -1}) kom upp. Vinsamlegast reyndu aftur.`);
      } finally {
        this.loading = false;
      }
    },
    openBulkModal() {
      const group = this.groupList.find(g => g.id === this.options.attendance.group);
      const groupLabel = group ? `${group.workplace.name} - ${group.name}` : '';
      const dateStr = moment(this.date).format('DD.MM.YYYY');
      this.$refs.bulkModal.show({
        title: `Setja fyrir alla - ${groupLabel} (${dateStr})`,
        maxHours: this.validate ? this.validate.max_hours : null,
      });
    },
    async onBulkSave({ hours, projectId }) {
      const date = moment(this.date).format('YYYY-MM-DD');
      const bulkHours = normalizeHoursInput(hours);
      this.loading = true;
      try {
        await Promise.all(
          this.studentList.map(s => {
            const existingHours = normalizeHoursInput(s.hours) ?? '0';
            const existingProject = s.attendance_project ? s.attendance_project.id : null;
            const data = {
              date,
              person: s.id,
              hours: bulkHours !== null ? bulkHours : existingHours,
              sick_hours: s.sick_hours != null ? String(s.sick_hours) : '0',
              attendance_type: s.attendance_type ? s.attendance_type.id : null,
              attendance_project: projectId !== null ? projectId : existingProject,
              comment: s.comment,
            };
            return s.attendance_id ? attendance.attendanceUpdate(s.attendance_id, data) : attendance.attendanceCreate(data);
          }),
        );
        await this.loadStudents();
        this.displaySuccess('Viðvera hefur verið uppfærð fyrir alla þátttakendur.');
      } catch (e) {
        this.$log.error(e);
        this.displayError(`Óvænt villa (${e.response ? e.response.status : -1}) kom upp. Vinsamlegast reyndu aftur.`);
      } finally {
        this.loading = false;
      }
    },
    onEdit(item) {
      const isPeriod = this.mode === 'period';
      const fallbackMaxHours = this.validate ? this.validate.max_hours : null;
      const fallbackBatch = this.validate ? !!this.validate.batch : false;
      this.$refs.editModal.show({
        personId: item.id,
        personName: item.person.name,
        groupId: isPeriod ? this.periodGroup : this.options.attendance.group,
        attendanceId: item.attendance_id || null,
        date: item.date || this.date,
        dateFrom: null,
        dateTo: null,
        hours: item.hours != null ? String(item.hours) : '',
        sick_hours: item.sick_hours != null ? String(item.sick_hours) : '',
        projectId: item.attendance_project ? item.attendance_project.id : null,
        absenceTypeId: item.attendance_type ? item.attendance_type.id : null,
        comment: item.comment || '',
        maxHours: item.maxHours != null ? item.maxHours : fallbackMaxHours,
        batch: item.batch != null ? !!item.batch : fallbackBatch,
      });
    },
    onEditSuccessful(updated) {
      if (this.mode === 'period') {
        const idx = this.periodRecords.findIndex(r => r.id === updated.attendanceId);
        if (idx !== -1) {
          this.$set(this.periodRecords, idx, {
            ...this.periodRecords[idx],
            hours: updated.hours,
            sick_hours: updated.sick_hours,
            comment: updated.comment,
            attendance_type: updated.attendance_type,
            attendance_project: updated.attendance_project,
          });
        } else {
          this.loadPeriodAttendance(this.periodPage);
        }
      } else {
        const idx = this.studentList.findIndex(s => s.id === updated.personId);
        if (idx !== -1) {
          const row = this.studentList[idx];
          const oldHours = parseFloat(row.hours || 0);
          const newHours = parseFloat(updated.hours || 0);
          const periodHoursTotal = row.period_hours_total != null ? row.period_hours_total - oldHours + newHours : null;
          this.$set(this.studentList, idx, {
            ...row,
            attendance_id: updated.attendanceId,
            hours: updated.hours,
            sick_hours: updated.sick_hours,
            comment: updated.comment,
            attendance_type: updated.attendance_type,
            attendance_project: updated.attendance_project,
            period_hours_total: periodHoursTotal,
          });
        } else {
          this.loadStudents();
        }
      }
    },
    // Period mode methods
    async onPeriodDateChange({ dateFrom, dateTo }) {
      this.periodDateFrom = dateFrom;
      this.periodDateTo = dateTo;
      this.periodRecords = [];
      if (dateFrom) {
        await this.loadPeriodGroups();
      }
      if (this.periodGroup && dateFrom && dateTo) {
        await this.loadPeriodAttendance(1);
      }
    },
    async loadPeriodGroups() {
      try {
        const response = await core.dropdownPlacementgroupList([], this.periodDateFrom);
        this.periodGroupList = response.data;
        if (this.periodGroup && !this.periodGroupList.find(g => g.id === this.periodGroup)) {
          this.periodGroup = null;
          this.periodRecords = [];
        }
      } catch (e) {
        this.$log.error(e);
        this.displayError(`Óvænt villa (${e.response ? e.response.status : -1}) kom upp. Vinsamlegast reyndu aftur.`);
      }
    },
    async loadPeriodAttendance(page, limit) {
      if (!this.periodGroup || !this.periodDateFrom || !this.periodDateTo) return;
      this.periodPage = page || 1;
      this.periodLimit = limit || this.periodLimit;
      this.loadingPeriod = true;
      this.periodRecords = [];
      this.periodDateValidations = {};
      try {
        const response = await attendance.attendanceListByPeriod(
          this.periodGroup,
          this.periodDateFrom,
          this.periodDateTo,
          this.periodPage,
          this.periodLimit,
        );
        this.periodPagination = parsePagination(response);
        const records = response.data;

        const uniqueDates = [...new Set(records.map(r => r.date))];
        const validations = await Promise.all(uniqueDates.map(date => placement.placementGroupDateValid(this.periodGroup, date)));
        const map = {};
        uniqueDates.forEach((date, i) => {
          map[date] = validations[i].data;
        });
        this.periodDateValidations = map;
        this.periodRecords = records;
      } catch (e) {
        this.$log.error(e);
        this.displayError(`Óvænt villa (${e.response ? e.response.status : -1}) kom upp. Vinsamlegast reyndu aftur.`);
      } finally {
        this.loadingPeriod = false;
      }
    },
    ...mapActions({
      displayError: 'displayError',
      displaySuccess: 'displaySuccess',
      saveLoggedInUserOptions: 'saveLoggedInUserOptions',
      setLoggedInUserOption: 'setLoggedInUserOption',
    }),
  },
  watch: {
    mode(newMode) {
      if (newMode === 'period') this.loadPeriodGroups();
    },
  },
  created() {
    this.options = {};

    this.options.attendance = {
      group: null,
      ...this.loggedInUserOptions.attendance,
    };

    if (this.$route.params.date) {
      this.date = moment(this.$route.params.date).toDate();
    }

    if (this.$route.params.group) {
      this.options.attendance.group = this.$route.params.group;
    }

    this.loadGroups();
    this.loadAttendanceProjects();
  },
};
</script>
